import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { AUDIENCES, IMAGES } from "../content";
import { EASE, Reveal } from "../motion";
import { Section, Wrap, SectionHead } from "./Section";
import { scrollToId } from "./Nav";

export default function AudienceSelector() {
  const [selected, setSelected] = useState(AUDIENCES[0]);

  return (
    <Section id="audience" theme="forest" className="py-16 sm:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img src={IMAGES.circle} alt="" className="w-full h-full object-cover opacity-[0.13]" loading="lazy" />
        <div className="absolute inset-0 bg-[#26342E]/72" />
      </div>
      <Wrap className="relative">
        <SectionHead
          light
          eyebrow="Personalisation without complexity"
          title="What brings you here today?"
          intro="One question, five honest answers. Choose one, the experience adapts around you, exactly as the future website will."
        />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 mt-10 sm:mt-12">
          <div className="lg:col-span-5" role="radiogroup" aria-label="Choose what brings you here">
            {AUDIENCES.map((a, i) => {
              const active = selected.id === a.id;
              return (
                <Reveal key={a.id} delay={i * 0.5}>
                  <button
                    data-testid={`audience-option-${a.id}`}
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSelected(a)}
                    className={`w-full text-left flex items-center justify-between gap-6 py-6 border-b transition-[border-color,background-color,padding] duration-400 cursor-pointer group ${
                      active ? "border-[#C29A4A]/60" : "border-[#F6F0E6]/10 hover:border-[#F6F0E6]/30"
                    }`}
                  >
                    <span className="flex items-baseline gap-5">
                      <span className={`text-[10px] font-bold tracking-[0.24em] transition-colors duration-300 ${active ? "text-[#C29A4A]" : "text-[#F6F0E6]/35"}`}>
                        0{i + 1}
                      </span>
                      <span className={`font-serif text-xl sm:text-2xl font-light transition-colors duration-300 ${active ? "text-[#F6F0E6]" : "text-[#F6F0E6]/55 group-hover:text-[#F6F0E6]/85"}`}>
                        {a.label}
                      </span>
                    </span>
                    <ArrowRight
                      size={17}
                      className={`shrink-0 transition-[transform,opacity,color] duration-400 ${
                        active ? "text-[#C29A4A] opacity-100 translate-x-0" : "text-[#F6F0E6]/40 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
                      }`}
                    />
                  </button>
                </Reveal>
              );
            })}
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.id}
                data-testid="audience-pathway-panel"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.55, ease: EASE }}
                className="bg-[#F6F0E6] rounded-3xl p-8 sm:p-12 text-[#242424]"
              >
                <div className="relative h-24 sm:h-28 rounded-2xl overflow-hidden mb-8 -mt-2" aria-hidden="true">
                  <img src={IMAGES[selected.img]} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-[#26342E]/15" />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#B66E50]">A sample pathway</p>
                <ol className="mt-8 space-y-0">
                  {selected.pathway.map((step, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: 18 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, ease: EASE, delay: 0.12 + i * 0.09 }}
                      className="flex gap-5 pb-6 last:pb-0"
                    >
                      <span className="flex flex-col items-center">
                        <span className="w-7 h-7 rounded-full border border-[#6F806E]/40 flex items-center justify-center text-[10px] font-bold text-[#4D5D4C] shrink-0">
                          {i + 1}
                        </span>
                        {i < selected.pathway.length - 1 && <span className="w-px flex-1 bg-[#6F806E]/25 mt-1.5" />}
                      </span>
                      <p className="text-[#242424]/80 leading-relaxed pt-1">{step}</p>
                    </motion.li>
                  ))}
                </ol>
                <motion.button
                  data-testid="audience-next-step"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.55, duration: 0.5 }}
                  onClick={() => scrollToId("concept")}
                  className="mt-9 inline-flex items-center gap-2.5 bg-[#26342E] text-[#F6F0E6] text-[11px] font-bold uppercase tracking-[0.18em] px-7 py-3.5 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#4D5D4C] hover:-translate-y-0.5 cursor-pointer"
                >
                  {selected.next}
                  <ArrowUpRight size={13} strokeWidth={2.5} />
                </motion.button>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Wrap>
    </Section>
  );
}
